import apiInst from '../api.js';

const SINGLE_FUNCTIONAL_NAME_ENDPOINT = '/ai/functional-name';
const BATCH_FUNCTIONAL_NAMES_ENDPOINT = '/ai/functional-names/batch';

/**
 * Client-side facade for AI functional-name generation.
 *
 * The OpenAI API key must never be available to the browser. This service only
 * calls authenticated backend endpoints; the backend owns the provider key,
 * model selection, prompt construction and rate limiting.
 *
 * Required backend contract:
 * - POST /ai/functional-name
 *   Body: { gpcDescription, productDescription, brandName? }
 *   Response: { functionalName: string } or { name: string }
 * - POST /ai/functional-names/batch
 *   Body: { products: [{ id, description, gpcDescription, brandName? }] }
 *   Response: { results: [{ productId|id, functionalName|name, error? }] }
 */
class AIService {
  async generateFunctionalName(gpcDescription, productDescription, brandName = null) {
    if (!this.isAvailable()) {
      throw new Error('AI Service not initialized. Check API environment variable.');
    }

    if (!gpcDescription || gpcDescription.trim() === '') {
      throw new Error('GPC description is required');
    }

    if (!productDescription || productDescription.trim() === '') {
      throw new Error('Product description is required');
    }

    try {
      const response = await apiInst.post(SINGLE_FUNCTIONAL_NAME_ENDPOINT, {
        gpcDescription,
        productDescription,
        brandName,
      });

      const functionalName = this.normalizeFunctionalName(
        response?.data?.functionalName || response?.data?.name
      );

      if (!functionalName) {
        throw new Error('AI response bevat geen functionele naam');
      }

      return functionalName;
    } catch (error) {
      throw this.toFriendlyError(error, 'AI generatie mislukt');
    }
  }

  async generateFunctionalNamesBatch(products) {
    if (!this.isAvailable()) {
      throw new Error('AI Service not initialized. Check API environment variable.');
    }

    if (!products || products.length === 0) {
      return [];
    }

    try {
      const response = await apiInst.post(BATCH_FUNCTIONAL_NAMES_ENDPOINT, {
        products,
      });

      const items = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.results)
          ? response.data.results
          : null;

      if (!items) {
        throw new Error('AI response bevat geen "results" array');
      }

      const resultMap = new Map();
      for (const item of items) {
        const productId = item?.productId || item?.id;
        if (!productId) continue;

        resultMap.set(String(productId), {
          functionalName: this.normalizeFunctionalName(item.functionalName || item.name),
          error: item.error || null,
        });
      }

      return products.map((product) => {
        const result = resultMap.get(String(product.id));
        return {
          productId: product.id,
          functionalName: result?.functionalName || null,
          error: result?.functionalName
            ? null
            : result?.error || 'Geen naam gegenereerd door AI',
        };
      });
    } catch (error) {
      throw this.toFriendlyError(error, 'AI batch generatie mislukt');
    }
  }

  isAvailable() {
    return !!process.env.VUE_APP_API_URL;
  }

  getModelName() {
    return 'backend-configured';
  }

  normalizeFunctionalName(value) {
    if (typeof value !== 'string') {
      return '';
    }

    const functionalName = value
      .trim()
      .replace(/^["']|["']$/g, '')
      .replace(/\.$/, '')
      .replace(/\n/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (functionalName.length < 2) {
      throw new Error('Generated functional name is too short');
    }

    if (functionalName.length > 100) {
      throw new Error('Generated functional name is too long');
    }

    return functionalName;
  }

  toFriendlyError(error, prefix) {
    const status = error?.response?.status || error?.status;
    const backendMessage = error?.response?.data?.message || error?.response?.data?.error;
    const msg = backendMessage || error?.message || '';

    if (status === 401 || status === 403 || msg.includes('API key') || msg.includes('Incorrect API key')) {
      return new Error('AI API authenticatie mislukt. Controleer de API configuratie.');
    } else if (status === 429 || msg.includes('quota') || msg.includes('rate limit')) {
      return new Error('AI API quota of rate limit bereikt. Probeer het later opnieuw.');
    } else if (msg.includes('network') || msg.includes('Network Error') || error?.code === 'ECONNREFUSED') {
      return new Error('Netwerkfout bij het verbinden met AI service.');
    }

    return new Error(`${prefix}: ${msg}`);
  }
}

export default new AIService();
